import Cookies from 'js-cookie';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { createApp } from './app';
import startZendeskApp from './app/interfaces/zendesk/zendesk';

// NOTE: If you try to import helpers into here you will create a circular dependency that will
//       break the app.

const COOKIE_OPTIONS = {
  path: '/',
  domain: '.matchbook.com',
  expires: 365
};

Object.assign(Cookies.defaults, COOKIE_OPTIONS);

// Reduces all BTags down to most appropriate one
const mbsource = Object.keys(Cookies.get())
  .filter(key => key.indexOf('MB_BTAG_') === 0)
  .map(key => Cookies.get(key).split('|'))
  .reduce(([prevValue, prevTimestamp], [value, timestamp]) => {
    if (parseInt(timestamp, 10) > parseInt(prevTimestamp, 10)) {
      return [value, parseInt(timestamp, 10)];
    }

    return [prevValue, prevTimestamp];
  }, ['', 0])
  .join('|');

if (mbsource !== '|0') {
  Cookies.set('mbsource', mbsource, COOKIE_OPTIONS);
}

// NOTE: This cookie tells the API that we are a UI
Cookies.set('mb-client-type', 'mb-web-ui', COOKIE_OPTIONS);

const clientId = Cookies.get('mb-client-id');
if (!clientId) {
  import('uuid/v4')
    .then(({ default: createUUID }) => Cookies.set('mb-client-id', createUUID(), COOKIE_OPTIONS))
    .catch((error) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('No UUID generated', error);
      }
    });
}

try {
  const supportedCurrencies = [
    'AUD',
    'BRL',
    'CAD',
    'CNY',
    'EUR',
    'GBP',
    'HKD',
    'IDR',
    'JPY',
    'KRW',
    'TWD',
    'USD',
  ];

  const supportedLanguages = [
    'en'
  ];
  // NOTE: This is to check if the culture is set correctly in local storage
  //      if not we remove it from localStorage altogether
  if (supportedCurrencies.includes(localStorage.getItem('currency'))) {
    localStorage.removeItem('currency');
  }

  if (supportedLanguages.includes(localStorage.getItem('language'))) {
    localStorage.removeItem('language');
  }
} catch (e) {
  // eslint-disable-next-line no-console
  console.error('Error in localStorage access', e);
}


// These generally are going to be modules that we have routes for

// Create wrapper for an application module to get full control over it's models
// const Application = createApp(require('modules/application/module'));
const Application = createApp();

const store = Application.bootStore();

const startApp = data => {
  Application.initialise(data);
};

const getDefaults = () => {
  // TODO: Get defaults from locale API if necessary
  try {
    return {
      currency: localStorage.getItem('mb-currency') || 'GBP',
      'odds-type': localStorage.getItem('mb-oddsType') || 'DECIMAL',
      'exchange-type': localStorage.getItem('mb-exchangeType') || 'back-lay',
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('error getting default settings', e);
    return {
      currency: 'GBP',
      'odds-type': 'DECIMAL',
      'exchange-type': 'back-lay',
    };
  }
};

const accountRequest = ({ sessionToken = '' }) => {
  fetch('/bpapi/rest/account', {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    }
  })
    .then((response) => {
      if (!response.ok) {
        return Promise.reject(response.status);
      }

      return response.json();
    })
    .then(json => ({ ...json, sessionToken, language: 'en' }))
    .then(data => {
      startApp(data);
    }, () => {
      startApp(getDefaults());
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('startup error:', err);
      startApp(getDefaults());
    });
};

fetch('/bpapi/rest/security/session', {
  credentials: 'same-origin',
  headers: {
    Accept: 'application/json'
  }
}).then((response) => {
  if (!response.ok) {
    try {
      // this is needed for users who have an active session-token before the httpOnly release
      Cookies.remove('session-token');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('could not remove session-token', e);
    }

    return Promise.reject(response.status);
  }

  return response.json();
})
  .then((json) => {
    if (json['session-token']) {
      accountRequest({ sessionToken: json['session-token'] });

      return;
    }

    startApp(getDefaults());
  })
  .catch((error) => {
    // eslint-disable-next-line no-console
    console.error('startup error:', error);
    startApp(getDefaults());
  });

let errorTrackingFiredOnce = false;

window.addEventListener('error', (event) => {
  if (!errorTrackingFiredOnce) {
    const trackingMessage = `${event.message} | ${event.filename} | ${event.colno} | ${event.lineno}`;
    const version = store.getState().getIn(['application', 'uiVersion']);

    track('Monitoring', 'UI Error', { description: trackingMessage, pathname: window.location.pathname, source: 'boot', mbui_version: version });
  }

  errorTrackingFiredOnce = true;
});

// start zendesk application;
startZendeskApp();

// Apple Pay Integration Nuvei
const applePayScript = document.createElement('script');
applePayScript.src = 'https://cdn.safecharge.com/safecharge_resources/v1/sc_applepay.min.js';
document.head.appendChild(applePayScript);

// TrustPilot widget
const trustpilotScript = document.createElement('script');
trustpilotScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
trustpilotScript.defer = true;
trustpilotScript.async = true;
document.body.appendChild(trustpilotScript);

window.zESettings = {
  webWidget: {
    offset: {
      horizontal: '-1000px',
      vertical: '0px',
      mobile: {
        horizontal: '-1000px',
        vertical: '0px'
      }
    },
    chat: {
      departments: {
        enabled: []
      }
    }
  }
};
